import { UntypedFormControl, ValidationErrors } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { Observable } from 'rxjs';
import { ErrorPosition, FieldType } from './builders/formly-builder.models';
import { DefaultValidatorFunctions, EmojiNotAllowed } from './validators/validators';

export function buildRow(
	fields: FormlyFieldConfig[],
	hideExpression?: boolean | string | ((model: any, formState: any, field?: FormlyFieldConfig) => boolean)
): FormlyFieldConfig {
	return {
		fieldGroupClassName: 'lu-row',
		fieldGroup: fields,
		hideExpression: hideExpression
	};
}

export function buildWrapped(formGroupLabel: string, fields: FormlyFieldConfig[]): FormlyFieldConfig {
	return {
		fieldGroup: fields,
		wrappers: ['form-group-wrapper'],
		templateOptions: {
			luFormGroupLabel: formGroupLabel
		}
	};
}

type FormlyValidationFn = (control: UntypedFormControl) => ValidationErrors;

export abstract class LuFormlyBuilder {
	protected abstract type: FieldType;

	protected key: string;
	protected className: string;
	protected hideExpression: boolean | string | ((model: any, formState: any, field?: FormlyFieldConfig) => boolean);
	protected hide: boolean;
	protected defaultValue: any;
	protected validators: FormlyValidationFn[] = [];
	protected defaultValidators: { [key: number]: FormlyValidationFn } = {
		[DefaultValidatorFunctions.EMOJI_NOT_ALLOWED]: EmojiNotAllowed
	};
	protected validator: any = {};

	// Template Options
	protected label: string;
	protected placeholder: string;
	protected required = false;
	protected tooltipMessage: string;
	protected disabled = false;
	protected readonly = false;
	protected change: any;
	protected attributes: { [key: string]: string | number };
	protected wrapperLabel: boolean;
	protected requiredMarker: boolean;
	protected errorPosition: ErrorPosition = ErrorPosition.RIGHT;

	// Expression Properties
	// tslint:disable-next-line:max-line-length
	protected expressionProperties: {
		[s: string]: string | ((model: any, formState: any, field?: FormlyFieldConfig) => any) | Observable<any>;
	} | null = null;

	constructor(key?: string, label?: string, placeholder?: string, className?: string) {
		this.setKey(key || null)
			.setLabel(label || null)
			.setPlaceholder(placeholder || null)
			.setClassName(className || null);
	}

	public setKey(key: string): this {
		this.key = key;
		return this;
	}

	public setType(type: FieldType): this {
		this.type = type;
		return this;
	}

	public setClassName(className: string): this {
		this.className = className;
		return this;
	}

	public setHideExpression(
		hideExpression: boolean | string | ((model: any, formState: any, field?: FormlyFieldConfig) => boolean)
	): this {
		this.hideExpression = hideExpression;
		return this;
	}

	public setHide(hide: boolean): this {
		this.hide = hide;
		return this;
	}

	public setDefaultValue(defaultValue: any): this {
		this.defaultValue = defaultValue;
		return this;
	}

	public setLabel(label: string): this {
		this.label = label;
		return this;
	}

	public setValidators(validators: FormlyValidationFn[]): this {
		this.validators = validators;
		return this;
	}

	public setValidatorObject(validator: any): this {
		this.validator = validator;
		return this;
	}

	public setPlaceholder(placeholder: string): this {
		this.placeholder = placeholder;
		return this;
	}

	public setRequired(required: boolean): this {
		this.required = required;
		return this;
	}

	public setTooltipMessage(tooltipMessage: string): this {
		this.tooltipMessage = tooltipMessage;
		return this;
	}

	public setDisabled(disabled: boolean): this {
		this.disabled = disabled;
		return this;
	}

	public setReadonly(readonly: boolean): this {
		this.readonly = readonly;
		return this;
	}

	public setAttributes(attributes: { [key: string]: string | number }): this {
		this.attributes = attributes;
		return this;
	}

	public hideWrapperLabel(hideWrapperLabel: boolean): this {
		this.wrapperLabel = hideWrapperLabel;
		return this;
	}

	public hideRequiredMarker(hideRequiredMarker: boolean): this {
		this.requiredMarker = hideRequiredMarker;
		return this;
	}

	public setExpressionProperty(
		property: string,
		// eslint-disable-next-line rxjs/finnish
		expression: string | ((model: any, formState: any, field?: FormlyFieldConfig) => any) | Observable<any>
	): this {
		if (this.expressionProperties === null) {
			this.expressionProperties = {};
		}

		this.expressionProperties[property] = expression;
		return this;
	}

	public ignoreDefaultValidators(validators: DefaultValidatorFunctions[]): this {
		if (validators?.length) {
			validators.forEach(key => {
				delete this.defaultValidators[key];
			});
		}
		return this;
	}

	public setErrorPosition(errorPosition: ErrorPosition): this {
		this.errorPosition = errorPosition;
		return this;
	}

	public build(): FormlyFieldConfig {
		if (this.type == null || this.key == null) {
			console.error('=== Field may not build correctly ===');
			if (this.type == null) {
				console.error('Field type is null or undefined');
			}

			if (this.key == null) {
				console.error('Field key is null or undefined');
			}

			console.error(this);
		}

		const field: FormlyFieldConfig = {
			key: this.key,
			type: this.type,
			className: this.className,
			defaultValue: this.defaultValue,
			validators: {
				validation: [
					...Object.keys(this.defaultValidators).map(key => this.defaultValidators[key]),
					...this.validators
				],
				...this.validator
			},
			templateOptions: {
				label: this.label,
				attributes: this.attributes,
				placeholder: this.placeholder,
				required: this.required,
				tooltipMessage: this.tooltipMessage,
				hideLabel: this.wrapperLabel,
				hideRequiredMarker: this.requiredMarker,
				errorPosition: this.errorPosition
			}
		};

		if (this.hideExpression !== null) {
			field.hideExpression = this.hideExpression;
		}

		if (this.hide !== null) {
			field.hide = this.hide;
		}

		if (this.disabled !== null) {
			field.templateOptions.disabled = this.disabled;
		}

		if (this.readonly !== null) {
			field.templateOptions.readonly = this.readonly;
		}

		if (this.expressionProperties !== null) {
			field.expressionProperties = this.expressionProperties;
		}

		return field;
	}
}
